import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  OutlinedInput,
  Chip,
  Autocomplete,
  IconButton,
  CircularProgress,
} from '@mui/material';
// import { useAtom } from 'jotai';
// import { itemSubCategoryAtom } from '../ItemCategoryList';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useGetItemCategories } from '../../Query/ItemCategoryQuery';
// import { useAllItemSubCategory } from '../..//Query/ItemSubCategoryQuery';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import MyEditor from '../../Stateless/RichTextEditor';
import { uploadImageWithPresignedUrl } from '../../HelperFunctions/s3Service';
import { useAddStoreItem, useEditStoreItem } from '../../Query/StoreItemQuery';
import {
  // AddOrEditItemRequestSchema,
  //AddOrEditItemRequestSchemaCurrencyCodeEnum,
  AddOrEditItemRequestSchemaItemAvailableTimeEnum,
  AddOrEditItemRequestSchemaItemSpecificationInner,
  AddOrEditItemRequestSchemaDiscountAvailableTimeEnum,
} from '../../../client-sdk';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { FormikErrors, FormikTouched } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useStoreManagementParams } from '../StoreManagement/useStoreManagementParams';
import { resizeImage } from '../Utils/resizeImage';
import { useStoreChoiceGroup } from '../../Query/StoreChoiceGroupQuery';
import CloseIcon from '@mui/icons-material/Close';
import { useContext } from 'react';
import { HimmaContext } from '../../../context/HimmaContext';

interface FormValues {
  restaurant: string;
  category: string;
  subcategory: string;
  itemCategoryId: string;
  itemSubCategoryId: string;
  restaurantId: string;
  itemName: string;
  itemNameArabic: string;
  itemContent: string;
  itemContentArabic: string;
  itemQuantity: string;
  currencyCode: string;
  price: string;
  discount: string;
  discountAvailableTime: string;
  discountAvailableDuration: { from: Dayjs | null; to: Dayjs | null };
  discountedPrice: string;
  isVeg: string;
  hasTax: string;
  taxName: string;
  taxNameArabic: string;
  taxPercentage: string;
  comboPack: string;
  halal: string;
  prescription: string;
  availableTime: AddOrEditItemRequestSchemaItemAvailableTimeEnum[];
  description: string;
  descriptionArabic: string;
  metaKeywords: string;
  metaKeywordsArabic: string;
  metaDescription: string;
  metaDescriptionArabic: string;
  chooseChoices: string;
  itemChoice: { value: string; label: string }[];
  addSpecification: string;
  itemImages: (File | string)[];
  specifications: AddOrEditItemRequestSchemaItemSpecificationInner[];
}

interface AddItemFormProps {
  onClose: () => void;
  mode?: 'add' | 'edit';
  initialData?: any;
}

type FormFieldName = keyof FormValues;
type FormFieldType = 'text' | 'select';

interface FormField {
  label: string;
  name: FormFieldName;
  type: FormFieldType;
  options?: string[];
  disabled?: boolean;
}

const AddItemForm: React.FC<AddItemFormProps> = ({
  onClose,
  mode = 'add',
  initialData,
}) => {
  const addStoreItemMutation = useAddStoreItem();
  const editStoreItemMutation = useEditStoreItem();
  const [loading, setLoading] = React.useState(false);
  const { showMessage } = useSnackbar();
  const apiClient = useContext(HimmaContext);
  const [page] = React.useState<number>(0);
  const [pageSize] = React.useState<number>(100);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const chooseFileInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null);

  const { params } = useStoreManagementParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const { data: manageStoreData } = useAllStores(memoizedParams);

  // Track selected store ID for dynamic category loading
  const [selectedStoreId, setSelectedStoreId] = useState<string>(initialData ? initialData.storeId : '');
  const [selectedStoreType, setSelectedStoreType] = useState<string>('');

  // Get item categories based on selected store
  const getItemCategory = useGetItemCategories({
    page: page + 1,
    limit: pageSize,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    statusFilter: 'All',
    addedByFilter: 'All',
    storeId: selectedStoreId, // Pass the selected store ID
  });

  // Derive state from props and data
  const categoryList = getItemCategory?.data?.data ?? [];

  // Improved loading state logic
  const isEditModeReady = useMemo(() => {
    if (mode !== 'edit') return true;

    // Check if we have all required data for edit mode
    const hasStoreData = Boolean(manageStoreData?.data);
    const isNotLoading = !getItemCategory.isLoading;
    const hasInitialData = Boolean(initialData?.storeId);
    const hasSelectedStoreId = Boolean(selectedStoreId);

    // Category data is optional - some stores might not have categories
    // Only require it if the API call is still loading or if we have categories
    const categoryDataReady = getItemCategory.isLoading ? false : true;

    return hasStoreData && categoryDataReady && isNotLoading && hasInitialData && hasSelectedStoreId;
  }, [mode, manageStoreData?.data, getItemCategory.isLoading, initialData?.storeId, selectedStoreId]);

  // Check if form is ready for submission
  const isFormReady = useMemo(() => {
    if (mode === 'edit') {
      return isEditModeReady && !getItemCategory.isLoading;
    }
    return isReady;
  }, [isEditModeReady, manageStoreData?.data, categoryList.length, getItemCategory.isLoading, mode, isReady]);

  // Improved loading state for edit mode data fetching
  const isEditModeLoading = useMemo(() => {
    if (mode !== 'edit') return false;

    // Show loading if we're still fetching data or if we don't have required data yet
    const isFetchingData = getItemCategory.isLoading;
    const missingRequiredData = !isEditModeReady;

    return isFetchingData || missingRequiredData;
  }, [mode, getItemCategory.isLoading, isEditModeReady]);

  // Add timeout to prevent infinite loading
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && isEditModeLoading) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [mode, isEditModeLoading]);

  // Filter subcategories based on selected category
  const allSubCategories = useMemo(() => {
    // For now, return all subcategories since we don't have formik values yet
    // This will be updated to filter based on selected category when formik is available
    return categoryList.flatMap((item) => item.subCategories ?? []);
  }, [categoryList]);
  console.log('allSubCategories', allSubCategories);



  const restaurantName = useMemo(() =>
    manageStoreData?.data
      ?.map((item) => item.nameEnglish)
      .filter((name): name is string => Boolean(name)) ?? [],
    [manageStoreData?.data]
  );


  const getRestaurantIdByName = useMemo(() =>
    (restaurantName: string) => {
      if (!manageStoreData?.data) return null;
      const restaurant = manageStoreData.data.find(
        (store) => store.nameEnglish === restaurantName
      );
      return restaurant?.id || null;
    },
    [manageStoreData?.data]
  );

  const getStoreTypeByName = useMemo(() =>
    (restaurantName: string) => {
      if (!manageStoreData?.data) return null;
      const restaurant = manageStoreData.data.find(
        (store) => store.nameEnglish === restaurantName
      );
      return restaurant?.restaurantDetails?.storeType || null;
    },
    [manageStoreData?.data]
  );

  // Derive initial restaurant name for edit mode
  const initialRestaurantName = useMemo(() => {
    if (mode === 'edit' && initialData?.storeId && manageStoreData?.data) {
      const restaurant = manageStoreData.data.find(
        (store) => store.id === initialData.storeId
      );
      return restaurant?.nameEnglish || initialData?.storeName || '';
    }
    return '';
  }, [mode, initialData?.storeId, initialData?.storeName, manageStoreData?.data]);

  // Derive initial category name for edit mode
  const initialCategoryName = useMemo(() => {
    if (mode === 'edit' && initialData?.itemCategoryId && categoryList.length > 0) {
      const category = categoryList.find(
        (cat) => cat.id === initialData.itemCategoryId
      );
      return category?.nameEnglish || '';
    }
    return '';
  }, [mode, initialData?.itemCategoryId, categoryList]);

  console.log('initialCategoryName', initialCategoryName);

  // Derive initial subcategory name for edit mode
  const initialSubcategoryName = useMemo(() => {
    if (mode === 'edit' && initialData?.itemSubCategoryId && initialData?.itemCategoryId && categoryList.length > 0) {
      // Find the category that contains this subcategory
      const category = categoryList.find(
        (cat) => cat.id === initialData.itemCategoryId
      );

      // Find the subcategory within that category
      const subcategory = category?.subCategories?.find(
        (sub) => sub.id === initialData.itemSubCategoryId
      );

      return subcategory?.nameEnglish || '';
    }
    return '';
  }, [mode, initialData?.itemSubCategoryId, initialData?.itemCategoryId, categoryList]);

  console.log('initialRestaurantName', initialRestaurantName, initialCategoryName, initialData);

  // Derive initial form values with correct initial names
  const initialFormValues = useMemo(() => ({
    restaurant: initialRestaurantName,
    category: initialCategoryName,
    subcategory: initialSubcategoryName,
    itemCategoryId: initialData?.itemCategoryId || '',
    itemSubCategoryId: initialData?.itemSubCategoryId || '',
    restaurantId: initialData?.storeId || '',
    itemName: initialData?.itemName || '',
    itemNameArabic: initialData?.itemNameArabic || '',
    itemContent: initialData?.itemContent || '',
    itemContentArabic: initialData?.itemContentArabic || '',
    itemQuantity: initialData?.itemQuantity?.toString() || '',
    currencyCode: 'JOD',
    price: initialData?.originalPrice?.toString() || '',
    discount: initialData?.discount ? 'Yes' : 'No',
    discountAvailableTime: initialData?.discountAvailableTime || 'always',
    discountAvailableDuration: initialData?.discountAvailableDuration
      ? {
        from: initialData.discountAvailableDuration.from
          ? dayjs(initialData.discountAvailableDuration.from)
          : null,
        to: initialData.discountAvailableDuration.to
          ? dayjs(initialData.discountAvailableDuration.to)
          : null,
      }
      : { from: null, to: null },
    discountedPrice: initialData?.discountedPrice?.toString() || '',
    isVeg: selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore'
      ? 'Veg'
      : initialData?.vegOrNonVeg === 'veg' ? 'Veg' : 'Nonveg',
    hasTax: initialData?.tax ? 'Yes' : 'No',
    taxName: initialData?.taxName || '',
    taxNameArabic: initialData?.taxNameArabic || '',
    taxPercentage: initialData?.taxPercentage || 0,
    comboPack: initialData?.comboPack ? 'Yes' : 'No',
    halal: selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore'
      ? 'No'
      : initialData?.isHalal ? 'Yes' : 'No',
    prescription: initialData?.requiresPrescription ? 'Yes' : 'No',
    availableTime: selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore'
      ? ['breakfast', 'brunch', 'lunch', 'supper', 'dinner'] as AddOrEditItemRequestSchemaItemAvailableTimeEnum[]
      : initialData?.itemAvailableTime || [],
    description: initialData?.description || '',
    descriptionArabic: initialData?.descriptionArabic || '',
    metaKeywords: initialData?.metaKeywords || '',
    metaKeywordsArabic: initialData?.metaKeyWordsArabic || '',
    metaDescription: initialData?.metaDescription || '',
    metaDescriptionArabic: initialData?.metaDescriptionArabic || '',
    chooseChoices: initialData?.hasChoice ? 'Yes' : 'No',
    itemChoice: [],
    addSpecification: initialData?.hasSpecification ? 'Yes' : 'No',
    itemImages: mode === 'edit' && initialData?.itemImage
      ? (Array.isArray(initialData.itemImage) ? initialData.itemImage : [initialData.itemImage])
        .filter((url: string) => url && typeof url === 'string' && !url.includes('undefined'))
      : [],
    specifications: initialData?.itemSpecification || [
      {
        specificationTitle: '',
        specificationDescription: '',
      },
    ],
  }), [initialData, mode, initialRestaurantName, initialCategoryName, initialSubcategoryName]);

  const formik = useFormik<FormValues>({
    enableReinitialize: isEditModeReady, // Only reinitialize when edit mode is ready
    initialValues: isEditModeReady ? initialFormValues : {
      // Provide empty defaults if not ready
      restaurant: '',
      category: '',
      subcategory: '',
      itemCategoryId: '',
      itemSubCategoryId: '',
      restaurantId: '',
      itemName: '',
      itemNameArabic: '',
      itemContent: '',
      itemContentArabic: '',
      itemQuantity: '',
      currencyCode: 'JOD',
      price: '',
      discount: 'No',
      discountAvailableTime: 'always',
      discountAvailableDuration: { from: null, to: null },
      discountedPrice: '',
      isVeg: selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore' ? 'Veg' : 'Veg',
      hasTax: 'No',
      taxName: '',
      taxNameArabic: '',
      taxPercentage: '0.0',
      comboPack: 'No',
      halal: selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore' ? 'No' : 'No',
      prescription: 'No',
      availableTime: selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore'
        ? ['breakfast', 'brunch', 'lunch', 'supper', 'dinner'] as AddOrEditItemRequestSchemaItemAvailableTimeEnum[]
        : [],
      description: '',
      descriptionArabic: '',
      metaKeywords: '',
      metaKeywordsArabic: '',
      metaDescription: '',
      metaDescriptionArabic: '',
      chooseChoices: 'No',
      itemChoice: [],
      addSpecification: 'No',
      itemImages: [],
      specifications: [
        {
          specificationTitle: '',
          specificationDescription: '',
        },
      ],
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object().shape({
      restaurant: Yup.string().required('Please select store'),
      category: Yup.string().required('Please select category'),
      subcategory: Yup.string().required('Please select subcategory'),
      itemName: Yup.string()
        .required('Please enter Item Name')
        .matches(/^[A-Za-z0-9\s]+$/, 'Item Name should only contain letters, numbers, and spaces')
        .max(50, 'Item Name cannot exceed 50 characters')
        .test(
          'not-only-spaces',
          'Item Name cannot be only spaces',
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        ),
      itemNameArabic: Yup.string()
        .required('Please enter Item Name in Arabic')
        .matches(
          /^[\u0600-\u06FF0-9\s]+$/,
          'Name should only contain Arabic letters, numbers, and spaces'
        )
        .max(50, 'Item Name cannot exceed 50 characters')
        .test(
          'not-only-spaces',
          'Name cannot be only spaces',
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        ),
      itemContent: Yup.string()
        .required('Please enter Item Content')
        // allow letters, numbers, spaces, and special characters
        .matches(/^[A-Za-z0-9\s\W]+$/, 'Item Content can contain letters, numbers, spaces, and special characters')
        .max(50, 'Item Content cannot exceed 50 characters')
        .test(
          'not-only-spaces',
          'Item Content cannot be only spaces',
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        )
        .test(
          'not-only-specials',
          'Item Content cannot be only special characters',
          (value) => {
            if (typeof value !== 'string') return false;
            // ensure at least one letter or number exists
            return /[A-Za-z0-9]/.test(value);
          }
        ),
      itemContentArabic: Yup.string()
        .required('Please enter Item content in Arabic')
        // allow Arabic letters, numbers, spaces, and special characters
        .matches(/^[\u0600-\u06FF0-9\s\W]+$/, 'Item content can contain Arabic letters, numbers, spaces, and special characters')
        .max(50, 'Item content cannot exceed 50 characters')
        .test(
          'not-only-spaces',
          'Item content cannot be only spaces',
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        )
        .test(
          'not-only-specials',
          'Item content cannot be only special characters',
          (value) => {
            if (typeof value !== 'string') return false;
            // ensure at least one Arabic letter or number exists
            return /[\u0600-\u06FF0-9]/.test(value);
          }
        ),
      itemQuantity: Yup.string()
        .required('Quantity is required')
        .matches(/^[0-9]+$/, 'Only numbers are allowed')
        .max(4, 'Quantity cannot exceed 4 digits')
        .test('max-length', 'Quantity cannot exceed 4 digits', (value) => {
          if (!value) return true;
          return value.length <= 4;
        })
        .test('is-positive', 'Quantity must be greater than 0', (value) => {
          if (!value) return true;
          return parseInt(value) > 0;
        }),
      price: Yup.string()
        .required('Price is required')
        .matches(/^\d*\.?\d+$/, 'Price must be a valid number')
        .test('max-length', 'Price cannot exceed 7 digits before decimal', (value) => {
          if (!value) return true;
          const [integerPart] = value.split('.');
          return integerPart.replace(/^0+/, '').length <= 7;
        })
        .test('is-positive', 'Price must be greater than 0', (value) => {
          if (!value) return true;
          return parseFloat(value) > 0;
        }),
      discount: Yup.string().required('Discount is required'),
      discountAvailableTime: Yup.string().when('discount', {
        is: 'Yes',
        then: (schema) =>
          schema.required('Discount available time is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      discountAvailableDuration: Yup.object().when(
        ['discount', 'discountAvailableTime'],
        {
          is: (discount: string, time: string) =>
            discount === 'Yes' && time === 'sometimes',
          then: (schema) =>
            schema.shape({
              from: Yup.date().required('Start date/time required'),
              to: Yup.date()
                .required('End date/time required')
                .min(Yup.ref('from'), 'End must be after start'),
            }),
          otherwise: (schema) => schema.notRequired(),
        }
      ),
      discountedPrice: Yup.string().when('discount', {
        is: 'Yes',
        then: (schema) =>
          schema
            .required('Discounted price is required')
            .matches(/^\d*\.?\d+$/, 'Discounted price must be a valid number')
            .test(
              'not-zero',
              'Discounted price cannot be 0',
              function (value) {
                if (!value) return false;
                return value !== '0' && value !== '0.';
              }
            )
            .test(
              'is-positive',
              'Discounted price must be greater than 0',
              function (value) {
                if (!value) return false;
                const numValue = parseFloat(value);
                return !isNaN(numValue) && numValue > 0;
              }
            )
            .test(
              'less-than-original',
              'Discounted price must be less than original price',
              function (value) {
                const originalPrice = this.parent.price;
                if (!value || !originalPrice) return true;
                const numValue = parseFloat(value);
                const numOriginal = parseFloat(originalPrice);
                return !isNaN(numValue) && !isNaN(numOriginal) && numValue < numOriginal;
              }
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      isVeg: Yup.string().test('isVeg-conditional', 'Select Veg/Nonveg', function () {
        // For pharmacy and grocery stores, skip validation
        // The validation will be handled in the component logic
        return true;
      }),
      hasTax: Yup.string().required('Select Tax status'),
      taxName: Yup.string().when('hasTax', {
        is: 'Yes',
        then: (schema) => schema
          .required('Tax Name is required')
          .matches(/^[A-Za-z0-9\s]+$/, 'Tax Name should only contain letters, numbers, and spaces')
          .max(50, 'Tax name cannot exceed 50 characters')
          .test(
            'not-only-spaces',
            'Tax Name cannot be only spaces',
            (value) => {
              if (typeof value !== 'string') return false;
              return value.trim().length > 0;
            }
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      taxNameArabic: Yup.string().when('hasTax', {
        is: 'Yes',
        then: (schema) => schema
          .required('Tax Name (Arabic) is required')
          .matches(/^[\u0600-\u06FF0-9\s]+$/, 'Tax Name should only contain Arabic letters, numbers, and spaces')
          .max(50, 'Tax name arabic cannot exceed 50 characters')
          .test(
            'not-only-spaces',
            'Tax Name cannot be only spaces',
            (value) => {
              if (typeof value !== 'string') return false;
              return value.trim().length > 0;
            }
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      taxPercentage: Yup.string().when('hasTax', {
        is: 'Yes',
        then: (schema) => schema
          .required('Tax Percentage is required')
          .matches(/^\d{1,2}(\.\d{0,2})?$/, 'Tax Percentage must be a number')
          .test(
              'is-positive',
              'Tax Percentage must be greater than 0',
              function (value) {
                if (!value) return false;
                const numValue = parseFloat(value);
                return !isNaN(numValue) && numValue > 0;
              }
            ).test(
              'is-max',
              'Tax Percentage must be less than 100',
              function (value) {
                if (!value) return false;
                const numValue = parseFloat(value);
                return !isNaN(numValue) && numValue < 100;
              }
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      comboPack: Yup.string().required('Combo pack selection is required'),
      halal: Yup.string().test('halal-conditional', 'Halal selection is required', function () {
        // For pharmacy and grocery stores, skip validation
        // The validation will be handled in the component logic
        return true;
      }),
      prescription: Yup.string().test('prescription-conditional', 'Prescription selection is required', function () {
        // Only validate for pharmacy stores
        const storeType = this.parent.storeType || selectedStoreType;
        if (storeType === 'PharmacyStore') {
          return this.parent.prescription === 'Yes' || this.parent.prescription === 'No';
        }
        return true;
      }),
      availableTime: Yup.array()
        .of(Yup.string().oneOf(['breakfast', 'brunch', 'lunch', 'supper', 'dinner']))
        .min(1, 'Select at least one available time'),
      description: Yup.string()
        .required('Description is required')
        .matches(
          /^[A-Za-z0-9][A-Za-z0-9\s,~`!@#$%^&*()_+\-={}\[\]:;"'<>,.?/\\|]*$/,
          'Description must start with a letter or number, and can contain spaces, commas, and special characters'
        )
        .test(
          'not-only-spaces',
          'Description cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      descriptionArabic: Yup.string()
        .required('Description are required')
        .matches(/^[\u0600-\u06FF0-9][\u0600-\u06FF0-9\s,~`!@#$%^&*()_+\-={}\[\]:;"'<>,.?/\\|\u2013\u2014]*$/, 'Description should only contain Arabic letters, numbers, spaces, and commas')
        .test(
          'not-only-spaces',
          'Description cannot be only spaces',
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        ),
      metaKeywords: Yup.string().notRequired(),
      metaKeywordsArabic: Yup.string().notRequired(),
      metaDescription: Yup.string().notRequired(),
      metaDescriptionArabic: Yup.string().notRequired(),
      chooseChoices: Yup.string().required('Select choices option'),
      itemChoice: Yup.array()
        .of(
          Yup.object().shape({
            value: Yup.string().required(),
            label: Yup.string().required(),
          })
        )
        .when('chooseChoices', {
          is: 'Yes',
          then: (schema) =>
            schema
              .min(1, 'Please select at least one item choice')
              .required('Please select at least one item choice'),
          otherwise: (schema) => schema.notRequired(),
        }),
      addSpecification: Yup.string().required('Select specification option'),
      itemImages: Yup.array()
        .min(1, 'At least one image is required')
        .max(5, 'Maximum 5 images allowed'),
      specifications: Yup.array().when('addSpecification', {
        is: 'Yes',
        then: (schema) =>
          schema.of(
            Yup.object().shape({
              specificationTitle: Yup.string()
                .required('Specification title is required')
                .matches(/^[A-Za-z0-9\s]+$/, 'Specification title should only contain letters, numbers, and spaces')
                .max(50, 'Specification title cannot exceed 50 characters')
                .test(
                  'not-only-spaces',
                  'Specification title cannot be only spaces',
                  (value) => {
                    if (typeof value !== 'string') return false;
                    return value.trim().length > 0;
                  }
                ),
              specificationDescription: Yup.string().required(
                'Specification description is required'
              ),
            })
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      console.log('Form submission started');
      console.log('Form values:', values);
      console.log('Form errors:', formik.errors);
      console.log('Form touched:', formik.touched);

      try {
        // Custom validation for conditional fields
        const validationErrors: any = {};

        // Validate isVeg for non-pharmacy/grocery stores
        if (selectedStoreType !== 'PharmacyStore' && selectedStoreType !== 'GroceryStore') {
          if (!values.isVeg || values.isVeg.trim() === '') {
            validationErrors.isVeg = 'Select Veg/Nonveg';
          }
        }

        // Validate halal for non-pharmacy/grocery stores
        if (selectedStoreType !== 'PharmacyStore' && selectedStoreType !== 'GroceryStore') {
          if (!values.halal || values.halal.trim() === '') {
            validationErrors.halal = 'Halal selection is required';
          }
        }

        // Validate availableTime - required for non-pharmacy/grocery stores
        // For pharmacy/grocery stores, it's auto-populated so validation is not needed
        if (selectedStoreType !== 'PharmacyStore' && selectedStoreType !== 'GroceryStore') {
          if (!values.availableTime || values.availableTime.length === 0) {
            validationErrors.availableTime = 'Select at least one available time';
          }
        }

        // Validate prescription for pharmacy stores
        if (selectedStoreType === 'PharmacyStore') {
          if (!values.prescription || values.prescription.trim() === '') {
            validationErrors.prescription = 'Prescription selection is required';
          }
        }

        // Check if form has validation errors
        const allErrors = { ...formik.errors, ...validationErrors };
        if (Object.keys(allErrors).length > 0) {
          console.error('Form has validation errors:', allErrors);
          // Set the validation errors
          Object.keys(validationErrors).forEach(key => {
            formik.setFieldError(key, validationErrors[key]);
            formik.setFieldTouched(key, true);
          });
          throw new Error('Form validation failed. Please check all required fields.');
        }

        // Check if required data is loaded
        if (!manageStoreData?.data) {
          console.error('Store data not loaded');
          throw new Error('Store data not loaded. Please refresh and try again.');
        }

        if (!filteredSubCategories) {
          console.error('Category data not loaded');
          throw new Error('Category data not loaded. Please refresh and try again.');
        }

        // Check if required selections are made
        if (!values.restaurant) {
          throw new Error('Please select a store');
        }

        if (!values.category) {
          throw new Error('Please select a category');
        }

        if (!values.subcategory) {
          throw new Error('Please select a subcategory');
        }

        // Check if images are uploaded
        if (!values.itemImages || values.itemImages.length === 0) {
          throw new Error('Please upload at least one item image');
        }

        // Check basic required fields
        if (!values.itemName || values.itemName.trim() === '') {
          throw new Error('Please enter item name');
        }

        if (!values.itemNameArabic || values.itemNameArabic.trim() === '') {
          throw new Error('Please enter item name in Arabic');
        }

        if (!values.itemContent || values.itemContent.trim() === '') {
          throw new Error('Please enter item content');
        }

        if (!values.itemContentArabic || values.itemContentArabic.trim() === '') {
          throw new Error('Please enter item content in Arabic');
        }

        if (!values.itemQuantity || values.itemQuantity.trim() === '') {
          throw new Error('Please enter item quantity');
        }

        if (!values.price || values.price.trim() === '') {
          throw new Error('Please enter item price');
        }

        // Check discount-related fields if discount is enabled
        if (values.discount === 'Yes') {
          if (!values.discountedPrice || values.discountedPrice.trim() === '') {
            throw new Error('Please enter discounted price');
          }

          // Validate that discounted price is not 0
          const discountedPriceNum = parseFloat(values.discountedPrice);
          if (isNaN(discountedPriceNum) || discountedPriceNum <= 0 || discountedPriceNum === 0) {
            throw new Error('Discounted price must be greater than 0');
          }

          // Validate that discounted price is less than original price
          const originalPriceNum = parseFloat(values.price);
          if (!isNaN(originalPriceNum) && discountedPriceNum >= originalPriceNum) {
            throw new Error('Discounted price must be less than original price');
          }

          if (values.discountAvailableTime === 'sometimes') {
            if (!values.discountAvailableDuration?.from || !values.discountAvailableDuration?.to) {
              throw new Error('Please select discount duration');
            }
          }
        }



        // Check description and meta fields
        if (!values.description || values.description.trim() === '') {
          throw new Error('Please enter description');
        }

        if (!values.descriptionArabic || values.descriptionArabic.trim() === '') {
          throw new Error('Please enter description in Arabic');
        }

        // Meta fields are optional; no blocking pre-submit checks

        // Check choice fields if choices are enabled
        if (values.chooseChoices === 'Yes') {
          if (!values.itemChoice || values.itemChoice.length === 0) {
            throw new Error('Please select at least one item choice');
          }
        }

        // Check specification fields if specifications are enabled
        if (values.addSpecification === 'Yes') {
          if (!values.specifications || values.specifications.length === 0) {
            throw new Error('Please add at least one specification');
          }

          // Check each specification
          for (let i = 0; i < values.specifications.length; i++) {
            const spec = values.specifications[i];
            if (!spec.specificationTitle || spec.specificationTitle.trim() === '') {
              throw new Error(`Please enter title for specification ${i + 1}`);
            }
            if (!spec.specificationDescription || spec.specificationDescription.trim() === '') {
              throw new Error(`Please enter description for specification ${i + 1}`);
            }
          }
        }

        console.log('Starting form submission process...');
        console.log('Selected store type:', selectedStoreType);
        console.log('Selected store ID:', selectedStoreId);
        setLoading(true);

        const imageUploadPromises = values.itemImages.map(async (file: File | string) => {
          if (file instanceof File) {
            if (!apiClient) {
              throw new Error('API client not available');
            }
            return await uploadImageWithPresignedUrl(file, apiClient);
          }
          return { url: file };
        });

        const uploadedImages = (await Promise.all(imageUploadPromises))
          .map((result: { url: string }) => result.url)
          .filter((url: string) => url && !url.includes('undefined')) as string[];

        // Get restaurant ID using the helper function
        const restaurantId = getRestaurantIdByName(values.restaurant);
        if (!restaurantId) {
          throw new Error('Selected restaurant not found');
        }

        // Get category and subcategory IDs
        const selectedCategory = categoryList.find(
          (cat) => cat.nameEnglish === values.category
        );
        const selectedSubCategory = filteredSubCategories.find(
          (sub) => sub.nameEnglish === values.subcategory
        );

        if (!selectedCategory?.id || !selectedSubCategory?.id) {
          throw new Error('Required selections not found');
        }

        const apiData: any = {
          // itemId: mode === 'edit' ? initialData?.id || '' : '',
          storeId: restaurantId,
          storeName: values.restaurant,
          itemCategoryId: selectedCategory.id,
          itemSubCategoryId: selectedSubCategory.id,
          itemName: values.itemName,
          itemNameArabic: values.itemNameArabic,
          itemContent: values.itemContent,
          itemContentArabic: values.itemContentArabic,
          itemQuantity: parseInt(values.itemQuantity) || 0,
          currencyCode: 'JOD',
          originalPrice: parseFloat(values.price) || 0,
          // Explicitly map discount fields so turning discount "off" fully clears any discount config
          discount: values.discount === 'Yes',
          discountedPrice:
            values.discount === 'Yes'
              ? parseFloat(values.discountedPrice) || 0
              : undefined,
          discountAvailableTime:
            values.discount === 'Yes'
              ? (values.discountAvailableTime as AddOrEditItemRequestSchemaDiscountAvailableTimeEnum)
              : undefined,
          discountAvailableDuration:
            values.discount === 'Yes' &&
              values.discountAvailableTime === 'sometimes'
              ? {
                from:
                  values.discountAvailableDuration?.from?.toISOString() ||
                  new Date().toISOString(),
                to:
                  values.discountAvailableDuration?.to?.toISOString() ||
                  new Date().toISOString(),
              }
              : undefined,
          ...(selectedStoreType !== 'PharmacyStore' && selectedStoreType !== 'GroceryStore' && {
            vegOrNonVeg: values.isVeg.toLowerCase() === 'veg' ? 'veg' : 'non-veg',
          }),
          tax: values.hasTax === 'Yes',
          taxName: values.taxName || '',
          taxNameArabic: values.taxNameArabic || '',
          taxPercentage: parseFloat(values.taxPercentage),
          comboPack: values.comboPack === 'Yes',
          isHalal: selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore'
            ? false
            : values.halal === 'Yes',
          requiresPrescription: selectedStoreType === 'PharmacyStore' ? values.prescription === 'Yes' : false,
          isStockAvailable: true,
          itemAvailableTime: values.availableTime || [],
          description: values.description,
          descriptionArabic: values.descriptionArabic,
          metaKeywords: values.metaKeywords,
          metaKeyWordsArabic: values.metaKeywordsArabic,
          metaDescription: values.metaDescription,
          metaDescriptionArabic: values.metaDescriptionArabic,
          hasChoice: values.chooseChoices === 'Yes',
          itemChoices: values.itemChoice.map((c) => c.value),
          hasSpecification: values.addSpecification === 'Yes',
          itemSpecification:
            values.addSpecification === 'Yes' ? values.specifications : [],
          itemImage: uploadedImages,
          addedBy: "Admin",
        };

        console.log('API data prepared:', apiData);

        if (mode === 'edit') {
          await editStoreItemMutation.mutateAsync({
            id: initialData.id,
            data: apiData,
          });
          showMessage('Item Updated Successfully', 'success');
        } else {
          await addStoreItemMutation.mutateAsync(apiData);
          showMessage('Item Added Successfully', 'success');
          // Reset form after successful creation
          formik.resetForm({
            values: {
              restaurant: '',
              category: '',
              subcategory: '',
              itemCategoryId: '',
              itemSubCategoryId: '',
              restaurantId: '',
              itemName: '',
              itemNameArabic: '',
              itemContent: '',
              itemContentArabic: '',
              itemQuantity: '',
              currencyCode: 'JOD',
              price: '',
              discount: 'No',
              discountAvailableTime: '',
              discountAvailableDuration: { from: null, to: null },
              discountedPrice: '',
              isVeg: '',
              hasTax: 'No',
              taxName: '',
              taxNameArabic: '',
              taxPercentage: '0',
              comboPack: 'No',
              halal: '',
              prescription: '',
              availableTime: [],
              description: '',
              descriptionArabic: '',
              metaKeywords: '',
              metaKeywordsArabic: '',
              metaDescription: '',
              metaDescriptionArabic: '',
              chooseChoices: 'No',
              itemChoice: [],
              addSpecification: 'No',
              itemImages: [],
              specifications: [],
            },
          });
        }
        onClose();
      } catch (error: any) {
        console.error('Error submitting form:', error);
        console.error('Error details:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
          stack: error?.stack
        });
        showMessage(
          error?.response?.data?.message ||
          error?.message ||
          (typeof error === 'string' ? error : 'Failed to save item. Please try again.'),
          'error'
        );
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  // Update selected store ID when restaurant selection changes
  useEffect(() => {
    if (formik.values.restaurant) {

      console.log("formik", formik.values.restaurant)
      const storeId = getRestaurantIdByName(formik.values.restaurant);
      const storeType = getStoreTypeByName(formik.values.restaurant);
      setSelectedStoreId(storeId || '');
      setSelectedStoreType(storeType || '');
    } else {
      setSelectedStoreId('');
    }
  }, [formik.values.restaurant, getRestaurantIdByName]);

  // Update form values when store type changes for pharmacy/grocery stores
  useEffect(() => {
    if (selectedStoreType === 'PharmacyStore' || selectedStoreType === 'GroceryStore') {
      // Set default values for hidden fields
      formik.setFieldValue('isVeg', 'Veg');
      formik.setFieldValue('halal', 'No');
      formik.setFieldValue('availableTime', ['breakfast', 'brunch', 'lunch', 'supper', 'dinner']);
    }
  }, [selectedStoreType]);



  // Set initial store ID and store type for edit mode
  useEffect(() => {
    if (mode === 'edit' && initialData?.storeId) {
      console.log('Setting selectedStoreId in edit mode:', initialData.storeId);
      setSelectedStoreId(initialData.storeId);

      // Set store type from initial data if available
      if (initialData?.storeType) {
        console.log('Setting selectedStoreType in edit mode:', initialData.storeType);
        setSelectedStoreType(initialData.storeType);
      } else if (manageStoreData?.data) {
        // Fallback: find store type from store data
        const store = manageStoreData.data.find(store => store.id === initialData.storeId);
        if (store?.restaurantDetails?.storeType) {
          console.log('Setting selectedStoreType from store data:', store.restaurantDetails.storeType);
          setSelectedStoreType(store.restaurantDetails.storeType);
        }
      }
    }
  }, [mode, initialData?.storeId, initialData?.storeType, manageStoreData?.data]);

  // Debug selectedStoreId changes
  useEffect(() => {
    console.log('selectedStoreId changed:', selectedStoreId);
  }, [selectedStoreId]);

  // Handle initial data loading for edit mode more reliably
  useEffect(() => {
    if (mode === 'edit' && initialData?.storeId && manageStoreData?.data && !selectedStoreId) {
      console.log('Initializing edit mode data...');

      // Set store ID
      setSelectedStoreId(initialData.storeId);

      // Set store type
      if (initialData?.storeType) {
        setSelectedStoreType(initialData.storeType);
      } else {
        const store = manageStoreData.data.find(store => store.id === initialData.storeId);
        if (store?.restaurantDetails?.storeType) {
          setSelectedStoreType(store.restaurantDetails.storeType);
        }
      }
    }
  }, [mode, initialData?.storeId, initialData?.storeType, manageStoreData?.data, selectedStoreId]);

  // Handle form initialization when data becomes ready
  useEffect(() => {
    if (mode === 'edit' && isEditModeReady && initialData) {
      console.log('Form data ready, initializing form values...');

      // Set restaurant name if not already set
      if (!formik.values.restaurant && initialRestaurantName) {
        formik.setFieldValue('restaurant', initialRestaurantName);
      }

      // Set category name if not already set
      if (!formik.values.category && initialCategoryName) {
        formik.setFieldValue('category', initialCategoryName);
      }

      // Set subcategory name if not already set
      if (!formik.values.subcategory && initialSubcategoryName) {
        formik.setFieldValue('subcategory', initialSubcategoryName);
      }
    }
  }, [mode, isEditModeReady, initialData, initialRestaurantName, initialCategoryName, initialSubcategoryName, formik.values.restaurant, formik.values.category, formik.values.subcategory]);

  // Track if this is the initial setup in edit mode
  const [isInitialSetup, setIsInitialSetup] = useState(true);

  // Reset isInitialSetup when mode changes
  useEffect(() => {
    setIsInitialSetup(true);
  }, [mode]);

  // Clear category and subcategory when store changes (but not during initial setup in edit mode)
  useEffect(() => {
    // Skip clearing during initial setup in edit mode
    if (formik.values.restaurant && !(mode === 'edit' && isInitialSetup)) {
      formik.setFieldValue('category', '');
      formik.setFieldValue('subcategory', '');
      formik.setFieldValue('itemCategoryId', '');
      formik.setFieldValue('itemSubCategoryId', '');
    }

    // Mark initial setup as complete after the first restaurant change
    if (formik.values.restaurant && mode === 'edit' && isInitialSetup) {
      setIsInitialSetup(false);
    }
  }, [formik.values.restaurant, mode, isInitialSetup]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formik.values.category) {
      // Find the selected category
      const selectedCategory = categoryList.find(
        (cat) => cat.nameEnglish === formik.values.category
      );

      // Get current subcategory value
      const currentSubcategory = formik.values.subcategory;

      // Check if current subcategory is valid for the new category
      const isValidSubcategory = selectedCategory?.subCategories?.some(
        (sub) => sub.nameEnglish === currentSubcategory
      );

      // Only clear subcategory if it's not valid for the new category
      if (!isValidSubcategory) {
        formik.setFieldValue('subcategory', '');
        formik.setFieldValue('itemSubCategoryId', '');
      }
    }
  }, [formik.values.category, categoryList, formik.values.subcategory]);

  // Handle category list loading in edit mode - Improved to prevent race conditions
  useEffect(() => {
    if (mode === 'edit' && categoryList.length > 0 && initialData?.itemCategoryId && isEditModeReady) {
      // Find the category by ID
      const category = categoryList.find(
        (cat) => cat.id === initialData.itemCategoryId
      );

      // If we found the category and the form doesn't have the category name set yet
      if (category?.nameEnglish && !formik.values.category) {
        console.log('Setting category from loaded data:', category.nameEnglish);
        formik.setFieldValue('category', category.nameEnglish);
      }

      // Also handle subcategory if we have the subcategory ID
      if (initialData?.itemSubCategoryId && category?.subCategories) {
        const subcategory = category.subCategories.find(
          (sub) => sub.id === initialData.itemSubCategoryId
        );

        if (subcategory?.nameEnglish && !formik.values.subcategory) {
          console.log('Setting subcategory from loaded data:', subcategory.nameEnglish);
          formik.setFieldValue('subcategory', subcategory.nameEnglish);
        }
      }
    }
  }, [mode, categoryList, initialData?.itemCategoryId, initialData?.itemSubCategoryId, formik.values.category, formik.values.subcategory, isEditModeReady]);

  // Create filtered subcategories based on selected category
  const filteredSubCategories = useMemo(() => {
    if (!formik.values.category) {
      return [];
    }

    // Find the selected category
    const selectedCategory = categoryList.find(
      (cat) => cat.nameEnglish === formik.values.category
    );

    // Return subcategories of the selected category only
    const subcategories = selectedCategory?.subCategories ?? [];


    return subcategories;
  }, [categoryList, formik.values.category]);

  // Fetch all choices for itemChoice dropdown (moved below formik)
  const selectedRestaurantId = useMemo(() => {
    if (!formik?.values?.restaurant || !manageStoreData?.data) return undefined;
    const restaurant = manageStoreData.data.find(
      (store) => store.nameEnglish === formik.values.restaurant
    );
    return restaurant?.id;
  }, [formik?.values?.restaurant, manageStoreData?.data]);

  // Only fetch choice groups if a restaurant is selected
  const { data: allChoicesGroupData } = useStoreChoiceGroup(
    selectedRestaurantId ? { storeId: selectedRestaurantId } : { storeId: '' }
  );
  const choiceOptions = useMemo(() => {
    const options = (allChoicesGroupData || []).map((choice: any) => ({
      label: choice.choiceGroupName,
      value: choice.choiceGroupId,
    })) || [];

    return options;
  }, [allChoicesGroupData, mode, selectedRestaurantId, initialData?.itemChoices]);

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    isAddMore = false
  ) => {
    const files = Array.from(e.target.files || []);
    formik.setFieldTouched("itemImages", true);

    if (files.length === 0) {
      formik.setFieldError("itemImages", "Please upload images");
      return;
    }

    // Get existing images from formik values
    const existing = formik.values.itemImages || [];

    // For "Add More", check if total would exceed 5 images
    if (isAddMore && existing.length + files.length > 5) {
      formik.setFieldError("itemImages", "Maximum 5 images allowed");
      return;
    }

    try {
      if (!apiClient) {
        throw new Error('API client not available');
      }
      const resizedFiles = await Promise.all(
        files.map((file) => resizeImage(file, "item image"))
      );
      const uploaded = await Promise.all(resizedFiles.map(file => uploadImageWithPresignedUrl(file, apiClient)));
      const urls = uploaded.map((res) => res.url);

      // If isAddMore is true, append to existing images, otherwise replace
      const newImages = isAddMore ? [...existing, ...urls] : urls;

      formik.setFieldValue("itemImages", newImages);
      formik.setFieldError("itemImages", undefined);

      // Reset the appropriate input's value
      if (isAddMore) {
        if (addMoreInputRef.current) {
          addMoreInputRef.current.value = '';
        }
      } else {
        if (chooseFileInputRef.current) {
          chooseFileInputRef.current.value = '';
        }
      }
    } catch (err) {
      console.error(err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
            ? err.message
            : `Error uploading Item Images`;
      formik.setFieldError("itemImages", errorMessage);
    }
  };

  useEffect(() => {
    const objectUrls: string[] = [];

    formik.values.itemImages.forEach((item) => {
      if (item instanceof File) {
        const url = URL.createObjectURL(item);
        objectUrls.push(url);
      }
    });

    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [formik.values.itemImages]);

  const images = Array.isArray(formik.values.itemImages)
    ? formik.values.itemImages.flat()
    : [];

  const handleAvailableTimeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    const current = formik.values.availableTime;
    const timeValue = value as AddOrEditItemRequestSchemaItemAvailableTimeEnum;

    if (current.includes(timeValue)) {
      formik.setFieldValue(
        'availableTime',
        current.filter((v) => v !== timeValue)
      );
    } else {
      formik.setFieldValue('availableTime', [...current, timeValue]);
    }
  };

  const handleAddSpecification = () => {
    if (formik.values.addSpecification === 'Yes') {
      const newSpecification: AddOrEditItemRequestSchemaItemSpecificationInner =
      {
        specificationTitle: '',
        specificationDescription: '',
        // specificationContents: '',
      };
      formik.setFieldValue('specifications', [
        ...formik.values.specifications,
        newSpecification,
      ]);
    }
  };

  const handleRemoveSpecification = (index: number) => {
    const newSpecifications = formik.values.specifications.filter(
      (_, i) => i !== index
    );
    formik.setFieldValue('specifications', newSpecifications);
  };

  const renderSpecificationField = (index: number) => {
    const spec = formik.values.specifications[index];
    const specError = formik.errors.specifications?.[index] as
      | FormikErrors<AddOrEditItemRequestSchemaItemSpecificationInner>
      | undefined;
    const specTouched = formik.touched.specifications?.[index] as
      | FormikTouched<AddOrEditItemRequestSchemaItemSpecificationInner>
      | undefined;

    return (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ width: '70%', display: 'flex', gap: 2 }}>
          <Box sx={{ width: '40%' }}>
            <TextField
              fullWidth
              name={`specifications[${index}].specificationTitle`}
              value={spec.specificationTitle}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 50) {
                  // Apply validation for specification title
                  if (!/^[A-Za-z0-9\s]+$/.test(value)) {
                    formik.setFieldError(`specifications[${index}].specificationTitle`, 'Specification title should only contain letters, numbers, and spaces');
                  } else if (value.trim().length === 0) {
                    formik.setFieldError(`specifications[${index}].specificationTitle`, 'Specification title cannot be only spaces');
                  } else {
                    formik.setFieldError(`specifications[${index}].specificationTitle`, undefined);
                  }
                  formik.handleChange(e);
                }
              }}
              onBlur={formik.handleBlur}
              placeholder="Title"
              error={
                formik.values.addSpecification === 'Yes' &&
                specTouched?.specificationTitle &&
                Boolean(specError?.specificationTitle)
              }
              helperText={
                formik.values.addSpecification === 'Yes' &&
                  specTouched?.specificationTitle &&
                  specError?.specificationTitle
                  ? String(specError.specificationTitle)
                  : ''
              }
              disabled={formik.values.addSpecification !== 'Yes'}
              inputProps={{
                maxLength: 50,
              }}
            />
          </Box>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 1 }}>
              <MyEditor
                value={spec.specificationDescription || ''}
                onChange={(val) =>
                  formik.setFieldValue(
                    `specifications[${index}].specificationDescription`,
                    val
                  )
                }
                onBlur={() =>
                  formik.setFieldTouched(
                    `specifications[${index}].specificationDescription`,
                    true
                  )
                }
              />
            </Box>
            {formik.values.addSpecification === 'Yes' &&
              specTouched?.specificationDescription &&
              specError?.specificationDescription && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {String(specError.specificationDescription)}
                </Typography>
              )}
          </Box>
          <Box
            sx={{ width: '20%' }}
            onClick={() => {
              if (formik.values.addSpecification === 'Yes') {
                handleRemoveSpecification(index);
              }
            }}
          >
            <DeleteIcon
              sx={{
                color:
                  formik.values.addSpecification === 'Yes'
                    ? 'grey'
                    : 'lightgrey',
                cursor:
                  formik.values.addSpecification === 'Yes'
                    ? 'pointer'
                    : 'not-allowed',
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const formFields: FormField[] = [
    {
      label: 'Select Store',
      name: 'restaurant',
      type: 'select',
      options: restaurantName,
    },
    {
      label: 'Select Category',
      name: 'category',
      type: 'select',
      options: categoryList.map((cat) => cat.nameEnglish).filter((name): name is string => Boolean(name)) as string[],
      disabled: !formik.values.restaurant || !selectedStoreId,
    },
    {
      label: 'Select Subcategory',
      name: 'subcategory',
      type: 'select',
      options: filteredSubCategories.map((sub) => sub.nameEnglish).filter((name): name is string => Boolean(name)) as string[],
      disabled: !formik.values.category || !formik.values.restaurant || !selectedStoreId || filteredSubCategories.length === 0,
    },
    { label: 'Item Name', name: 'itemName', type: 'text' },
    {
      label: 'Item Name (In Arabic)',
      name: 'itemNameArabic',
      type: 'text',
    },
    { label: 'Item Content', name: 'itemContent', type: 'text' },
    {
      label: 'Item Content (In Arabic)',
      name: 'itemContentArabic',
      type: 'text',
    },
    { label: 'Item Quantity', name: 'itemQuantity', type: 'text' },
    {
      label: 'Currency Code',
      name: 'currencyCode',
      type: 'select',
      options: ['JOD'],
      disabled: true,
    },
    { label: 'Original Price', name: 'price', type: 'text' },
    {
      label: 'Discount',
      name: 'discount',
      type: 'select',
      options: ['Yes', 'No'],
    },
  ] as const;

  const [autoFocus, setAutoFocus] = useState<{ [key: string]: boolean }>({});

  function isValidOption(value: string, options: string[] = []) {
    const val = (value || '').trim().toLowerCase();
    return options.some((opt) => opt.trim().toLowerCase() === val);
  }

  const renderFormField = (field: FormField) => {
    const fieldName = field.name;
    const touched = formik.touched[fieldName] ?? false;
    const error = formik.errors[fieldName] ?? '';
    const value = formik.values[fieldName];

    // Convert value to string for display
    const displayValue = (() => {
      if (value === undefined || value === null) return '';
      if (Array.isArray(value)) return '';
      if (typeof value === 'number') return `${value}`;
      return String(value);
    })();

    return (
      <Box
        key={fieldName}
        sx={{ display: 'flex', alignItems: 'center', mb: (fieldName === 'restaurant' || fieldName === 'category' || fieldName === 'subcategory') ? 1 : 2 }}
      >
        <Box sx={{ width: '30%', pr: 2 }}>
          <Typography
            dangerouslySetInnerHTML={{
              __html: `${field.label} <span style="color: red">*</span>`,
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          {field.type === 'select' ? (
            <FormControl fullWidth error={Boolean(touched) && Boolean(error)}>
              {(fieldName === 'restaurant' || fieldName === 'category' || fieldName === 'subcategory') ? (
                <Autocomplete
                  options={field.options || []}
                  getOptionLabel={(option) => option}
                  value={(() => {
                    const val = (formik.values[fieldName] || '').trim();
                    if (!val) return null;

                    // First try to find an exact match in the current options
                    const match = (field.options || []).find(
                      (opt) => opt.trim().toLowerCase() === val.toLowerCase()
                    );

                    if (match) return match;

                    // If no match found in current options, but we have a value in formik,
                    // return the formik value as a fallback (this handles the case where
                    // options haven't loaded yet but we have the initial value)
                    if (val && mode === 'edit') {
                      return val;
                    }

                    return null;
                  })()}
                  onFocus={() => setAutoFocus((prev) => ({ ...prev, [fieldName]: true }))}
                  onBlur={() => {
                    setAutoFocus((prev) => ({ ...prev, [fieldName]: false }));
                    const val = (formik.values[fieldName] || '').trim().toLowerCase();
                    const match = (field.options || []).find(
                      (opt) => opt.trim().toLowerCase() === val
                    );
                    if (!match) {
                      formik.setFieldValue(fieldName, '');
                    }
                    formik.setFieldTouched(fieldName, true, true);
                  }}
                  onChange={(_, newValue) => {
                    formik.setFieldValue(fieldName, newValue || '');
                    formik.setFieldTouched(fieldName, true, false);

                    // Clear error if the new value is valid
                    const isValid = (field.options || []).some(
                      (opt) => opt.trim().toLowerCase() === (newValue || '').trim().toLowerCase()
                    );
                    if (isValid) {
                      formik.setFieldError(fieldName, undefined);
                      formik.setFieldTouched(fieldName, undefined);
                    }

                    // Handle special fields
                    if (field.name === 'restaurant') {
                      // If you have a handler, call it here
                      // handleRestaurantChange(newValue);
                    } else if (field.name === 'category') {
                      // handleCategoryChange(newValue);
                    } else if (field.name === 'subcategory') {
                      // handleSubcategoryChange(newValue);
                    }
                  }}
                  disabled={field.disabled}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.trim().toLowerCase() === value.trim().toLowerCase();
                  }}
                  filterOptions={(options, state) => {
                    return options.filter((option) =>
                      option.toLowerCase().includes(state.inputValue.toLowerCase())
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder={
                        fieldName === 'category' && !selectedStoreId
                          ? 'Please select a store first'
                          : fieldName === 'category' && getItemCategory.isLoading
                            ? 'Loading categories...'
                            : fieldName === 'subcategory' && !formik.values.category
                              ? 'Please select a category first'
                              : fieldName === 'subcategory' && formik.values.category && filteredSubCategories.length === 0
                                ? 'No sub category'
                                : `Select ${field.label.replace('Select ', '')}`
                      }
                      error={Boolean(touched) && !autoFocus[fieldName] && !isValidOption(formik.values[fieldName], field.options)}
                      helperText={
                        fieldName === 'category' && !selectedStoreId
                          ? 'Please select a store to view categories'
                          : fieldName === 'category' && getItemCategory.isLoading
                            ? 'Loading categories...'
                            : fieldName === 'subcategory' && !formik.values.category
                              ? 'Please select a category to view subcategories'
                              : fieldName === 'subcategory' && formik.values.category && filteredSubCategories.length === 0
                                ? 'No sub categories available for this category'
                                : Boolean(touched) && !autoFocus[fieldName] && !isValidOption(formik.values[fieldName], field.options)
                                  ? String(error)
                                  : ''
                      }
                      sx={{
                        height: 50,
                        width: '100%',
                        fontSize: '1rem',
                        filter: field.disabled
                          ? 'blur(0.8px) grayscale(0.5)'
                          : 'none',
                        '& .MuiInputBase-root': {
                          height: 35,
                          width: "100%",
                          marginTop: "15px"
                        },
                      }}
                    />
                  )}
                  sx={{
                    width: '100%',
                    filter: field.disabled
                      ? 'blur(0.8px) grayscale(0.5)'
                      : 'none',
                  }}
                  loading={fieldName === 'category' && getItemCategory.isLoading}
                />
              ) : (
                <Select<string>
                  name={fieldName}
                  value={displayValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    formik.handleChange(e);

                    // Special handling for discount field
                    if (fieldName === 'discount') {
                      if (newValue === 'No') {
                        // Clear discount-related fields when discount is set to No
                        formik.setFieldValue('discountedPrice', '');
                        formik.setFieldValue('discountAvailableTime', '');
                        formik.setFieldValue('discountAvailableDuration', { from: null, to: null });
                        formik.setFieldError('discountedPrice', undefined);
                        formik.setFieldError('discountAvailableTime', undefined);
                        formik.setFieldError('discountAvailableDuration', undefined);
                      } else if (newValue === 'Yes') {
                        // Trigger validation for discount fields when discount is set to Yes
                        setTimeout(() => {
                          formik.validateField('discountedPrice');
                          formik.validateField('discountAvailableTime');
                        }, 100);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    formik.setFieldTouched(fieldName, true, true);
                  }}
                  displayEmpty
                  disabled={field.disabled}
                  sx={{
                    height: 50,
                    width: '100%',
                    fontSize: '1rem',
                    filter: field.disabled
                      ? 'blur(0.8px) grayscale(0.5)'
                      : 'none',
                    backgroundColor: field.disabled ? '#f5f5f5' : undefined,
                  }}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {field.options?.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {(fieldName !== 'category' && fieldName !== 'subcategory' && fieldName !== 'restaurant') && (
                <FormHelperText>
                  {touched && error ? String(error) : ''}
                </FormHelperText>
              )}
            </FormControl>
          ) : (
            <TextField
              fullWidth
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  width: '100%',
                },
              }}
              name={fieldName}
              value={displayValue}
              onChange={(e) => {
                const value = e.target.value;

                // Apply real-time validation for specific fields
                if (['itemName', 'itemNameArabic', 'itemQuantity', 'price', 'taxName', 'taxNameArabic', 'taxPercentage', 'metaKeywords', 'metaKeywordsArabic', 'metaDescription', 'metaDescriptionArabic'].includes(fieldName)) {
                  handleFieldChange(fieldName, value);
                } else {
                  // For other fields, use existing logic
                  if (fieldName === 'itemQuantity' || fieldName === 'price') {
                    if (value === '' || /^[0-9]+$/.test(value)) {
                      if (fieldName === 'itemQuantity' && value.length <= 4) {
                        formik.handleChange(e);
                      } else if (fieldName === 'price' && value.length <= 7) {
                        formik.handleChange(e);
                      }
                    }
                  } else {
                    formik.handleChange(e);
                  }
                }
              }}
              onBlur={(e) => {
                formik.handleBlur(e);
                formik.setFieldTouched(fieldName, true, true);
              }}
              placeholder={
                fieldName === 'itemContent' || fieldName === 'itemContentArabic'
                  ? 'e.g 500ml, 1 piece, 2 slices'
                  : fieldName === 'itemQuantity'
                    ? 'e.g 1 kg,1 liter, 250mg'
                    : fieldName.replace(/([A-Z])/g, ' $1')
              }

              error={Boolean(touched) && Boolean(error)}
              helperText={touched && error ? String(error) : ''}
              inputProps={{
                maxLength:
                  fieldName === 'itemQuantity' || fieldName === 'price'
                    ? 10
                    : 50,
                inputMode:
                  fieldName === 'itemQuantity'
                    ? 'numeric'
                    : fieldName === 'price'
                      ? 'decimal'
                      : 'text',
                pattern:
                  fieldName === 'itemQuantity'
                    ? '[0-9]*'
                    : fieldName === 'price'
                      ? '[0-9.]*'
                      : undefined,
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  // Render discount fields if discount is Yes
  const renderDiscountFields = () => (
    <>
      {/* Discount Available Time */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ width: '30%', pr: 2 }}>
          <Typography>Discount Available Time *</Typography>
        </Box>
        <Box sx={{ width: '70%' }}>
          <FormControl
            error={
              Boolean(formik.touched.discountAvailableTime) &&
              Boolean(formik.errors.discountAvailableTime)
            }
          >
            <RadioGroup
              row
              name="discountAvailableTime"
              value={formik.values.discountAvailableTime}
              onChange={formik.handleChange}
            >
              <FormControlLabel
                value="always"
                control={<Radio />}
                label="Always"
              />
              <FormControlLabel
                value="sometimes"
                control={<Radio />}
                label="Sometimes"
              />
            </RadioGroup>
            <FormHelperText>
              {formik.touched.discountAvailableTime &&
                formik.errors.discountAvailableTime
                ? String(formik.errors.discountAvailableTime)
                : ''}
            </FormHelperText>
          </FormControl>
        </Box>
      </Box>
      {/* Discount Available Duration (only if Sometimes is selected) */}
      {formik.values.discountAvailableTime === 'sometimes' && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ width: '30%', pr: 1.5 }}>
            <Typography>Discount Available Duration *</Typography>
          </Box>
          <Box sx={{ width: '60%', display: 'flex', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                value={formik.values.discountAvailableDuration?.from}
                onChange={(value: Dayjs | null) =>
                  formik.setFieldValue('discountAvailableDuration', {
                    ...formik.values.discountAvailableDuration,
                    from: value,
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      height: 10,
                      '& .MuiInputBase-root': { height: 10, minHeight: 10 },
                    },
                  },
                }}
              />
              <DateTimePicker
                value={formik.values.discountAvailableDuration?.to}
                onChange={(value: Dayjs | null) =>
                  formik.setFieldValue('discountAvailableDuration', {
                    ...formik.values.discountAvailableDuration,
                    to: value,
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      height: 40,
                      '& .MuiInputBase-root': { height: 40, minHeight: 40 },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            {formik.touched.discountAvailableDuration &&
              formik.errors.discountAvailableDuration && (
                <FormHelperText error>
                  {typeof formik.errors.discountAvailableDuration === 'string'
                    ? formik.errors.discountAvailableDuration
                    : ''}
                </FormHelperText>
              )}
          </Box>
        </Box>
      )}
      {/* Discounted Price */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ width: '30%', pr: 2 }}>
          <Typography>Discounted Price *</Typography>
        </Box>
        <Box sx={{ width: '70%' }}>
          <TextField
            fullWidth
            name="discountedPrice"
            value={formik.values.discountedPrice}
            onChange={(e) => {
              handleFieldChange('discountedPrice', e.target.value);
            }}
            placeholder="Discounted Price"
            InputProps={{
              startAdornment: <span style={{ marginRight: 8 }}>JOD</span>,
            }}
            error={
              Boolean(formik.touched.discountedPrice) &&
              Boolean(formik.errors.discountedPrice)
            }
            helperText={
              formik.touched.discountedPrice && formik.errors.discountedPrice
                ? String(formik.errors.discountedPrice)
                : ''
            }
          />
        </Box>
      </Box>
    </>
  );

  const handleClear = () => {
    formik.setFieldValue('itemImages', []);
    formik.setFieldError('itemImages', undefined);
    // Reset both input values
    if (chooseFileInputRef.current) {
      chooseFileInputRef.current.value = '';
    }
    if (addMoreInputRef.current) {
      addMoreInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = formik.values.itemImages.filter((_, i) => i !== index);
    formik.setFieldValue('itemImages', newImages);

    // Clear validation error if no images left
    if (newImages.length === 0) {
      formik.setFieldError('itemImages', undefined);
    }
  };

  useEffect(() => {
    if (
      mode === 'edit' &&
      initialRestaurantName &&
      formik.values.restaurant !== initialRestaurantName
    ) {
      formik.setFieldValue('restaurant', initialRestaurantName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRestaurantName, mode]);

  // Use choiceGroups for mapping, supporting both array of IDs and array of objects
  const itemChoices = Array.isArray(initialData?.choiceGroups)
    ? initialData.choiceGroups.map((cg: any) => cg.choiceGroupId || cg)
    : [];

  useEffect(() => {
    if (
      mode === 'edit' &&
      itemChoices.length > 0 &&
      choiceOptions.length > 0 &&
      formik.values.restaurant &&
      selectedRestaurantId
    ) {
      const matchedChoices = itemChoices
        .map((choiceId: string) => {
          const match = choiceOptions.find((opt) => opt.value === choiceId);
          return match;
        })
        .filter((choice: { value: string; label: string } | undefined) => choice !== undefined);

      if (matchedChoices.length > 0 && formik.values.itemChoice.length === 0) {
        formik.setFieldValue('itemChoice', matchedChoices);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choiceOptions, mode, itemChoices, formik.values.restaurant, selectedRestaurantId]);

  // Additional effect to handle choice group updates when restaurant changes
  useEffect(() => {
    if (mode === 'edit' && formik.values.restaurant && choiceOptions.length > 0 && formik.values.itemChoice.length > 0) {
      // Check if current itemChoice values still exist in the new choiceOptions
      const validChoices = formik.values.itemChoice.filter(choice =>
        choiceOptions.some(option => option.value === choice.value)
      );

      // If some choices are no longer valid, update the field
      if (validChoices.length !== formik.values.itemChoice.length) {
        formik.setFieldValue('itemChoice', validChoices);
        formik.setFieldTouched('itemChoice', true, true);
      }
    }
  }, [choiceOptions, formik.values.restaurant, mode, formik.values.itemChoice.length]);

  // Reset choice group when restaurant changes
  useEffect(() => {
    if (formik.values.chooseChoices === 'Yes') {
      formik.setFieldValue('itemChoice', []);
      formik.setFieldTouched('itemChoice', false);
    }
  }, [formik.values.restaurant, formik.values.chooseChoices]);

  const [choiceSelectOpen, setChoiceSelectOpen] = useState(false);

  // Real-time validation functions
  const validateField = (fieldName: string, value: string): string | undefined => {
    if (!value) return undefined;

    switch (fieldName) {

      case 'itemName':
        // Only allow letters, numbers, and spaces
        if (!/^[A-Za-z0-9\s]+$/.test(value)) {
          return 'Item Name should only contain letters, numbers, and spaces';
        }
        if (value.trim().length === 0) {
          return 'Item Name cannot be only spaces';
        }
        break;

      case 'itemNameArabic':
        // Only allow Arabic letters, numbers, and spaces
        if (!/^[\u0600-\u06FF0-9\s]+$/.test(value)) {
          return 'Name should only contain Arabic letters, numbers, and spaces';
        }
        if (value.trim().length === 0) {
          return 'Name cannot be only spaces';
        }
        break;

      case 'itemQuantity':
        // Only allow numbers
        if (!/^[0-9]+$/.test(value)) {
          return 'Only numbers are allowed';
        }
        if (parseInt(value) <= 0) {
          return 'Quantity must be greater than 0';
        }
        break;

      case 'price':
        // Allow numbers with optional decimal
        if (!/^\d*\.?\d+$/.test(value)) {
          return 'Price must be a valid number';
        }
        if (parseFloat(value) <= 0) {
          return 'Price must be greater than 0';
        }
        break;

      case 'discountedPrice':
        // Prevent empty string or just "0"
        if (value === '' || value === '0' || value === '0.') {
          return 'Discounted price must be greater than 0';
        }
        // Allow numbers with optional decimal
        if (!/^\d*\.?\d+$/.test(value)) {
          return 'Discounted price must be a valid number';
        }
        if (parseFloat(value) <= 0 || parseFloat(value) === 0) {
          return 'Discounted price must be greater than 0';
        }
        break;

      case 'taxName':
        // Only allow letters, numbers, and spaces
        if (!/^[A-Za-z0-9\s]+$/.test(value)) {
          return 'Tax Name should only contain letters, numbers, and spaces';
        }
        if (value.trim().length === 0) {
          return 'Tax Name cannot be only spaces';
        }
        break;

      case 'taxNameArabic':
        // Only allow Arabic letters, numbers, and spaces
        if (!/^[\u0600-\u06FF0-9\s]+$/.test(value)) {
          return 'Tax Name should only contain Arabic letters, numbers, and spaces';
        }
        if (value.trim().length === 0) {
          return 'Tax Name cannot be only spaces';
        }
        break;

      case 'taxPercentage':
        // Only allow numbers and decimal point
        if (!/^\d*\.?\d*$/.test(value)) {
          return 'Tax Percentage should only contain numbers';
        }
        const taxValue = parseFloat(value);
        if (isNaN(taxValue)) {
          return 'Tax Percentage must be a valid number';
        }
        if (taxValue < 0) {
          return 'Tax percentage cannot be negative';
        }
        if (taxValue > 100) {
          return 'Tax percentage cannot exceed 100';
        }
        break;

      // case 'metaKeywords':
      //   // Only allow letters, numbers, spaces, and commas
      //   if (!/^[A-Za-z0-9\s,]+$/.test(value)) {
      //     return 'Meta Keywords should only contain letters, numbers, spaces, and commas';
      //   }
      //   if (value.trim().length === 0) {
      //     return 'Meta Keywords cannot be only spaces';
      //   }
      //   break;

      // case 'metaKeywordsArabic':
      //   // Only allow Arabic letters, numbers, spaces, and commas
      //   if (!/^[\u0600-\u06FF0-9\s,]+$/.test(value)) {
      //     return 'Meta Keywords should only contain Arabic letters, numbers, spaces, and commas';
      //   }
      //   if (value.trim().length === 0) {
      //     return 'Meta Keywords cannot be only spaces';
      //   }
      //   break;

      // case 'metaDescription':
      //   // Only allow letters, numbers, spaces, and basic punctuation
      //   if (!/^[A-Za-z0-9\s.,!?-]+$/.test(value)) {
      //     return 'Meta Description should only contain letters, numbers, spaces, and basic punctuation';
      //   }
      //   if (value.trim().length === 0) {
      //     return 'Meta Description cannot be only spaces';
      //   }
      //   break;

      // case 'metaDescriptionArabic':
      //   // Only allow Arabic letters, numbers, spaces, and basic punctuation
      //   if (!/^[\u0600-\u06FF0-9\s.,!?-]+$/.test(value)) {
      //     return 'Meta Description should only contain Arabic letters, numbers, spaces, and basic punctuation';
      //   }
      //   if (value.trim().length === 0) {
      //     return 'Meta Description cannot be only spaces';
      //   }
      //   break;
    }

    return undefined;
  };

  // Enhanced onChange handler for real-time validation
  const handleFieldChange = (fieldName: string, value: string) => {
    // Special handling for discountedPrice to prevent 0
    if (fieldName === 'discountedPrice' && formik.values.discount === 'Yes') {
      // Prevent "0" from being entered
      if (value === '0' || value === '0.') {
        formik.setFieldError(fieldName, 'Discounted price must be greater than 0');
        formik.setFieldTouched(fieldName, true, false);
        return; // Don't update the value
      }
    }

    // Apply validation based on field type
    const validationError = validateField(fieldName, value);

    // Convert value to appropriate type for specific fields
    let processedValue: any = value;
    if (fieldName === 'taxPercentage') {
      // Convert to number for taxPercentage field
        processedValue = value;
    }

    // Update the field value
    formik.setFieldValue(fieldName, processedValue);

    // Set validation error if any
    if (validationError) {
      formik.setFieldError(fieldName, validationError);
    } else {
      // Clear error if validation passes
      formik.setFieldError(fieldName, undefined);
    }

    // Mark field as touched for immediate feedback
    formik.setFieldTouched(fieldName, true, false);
  };

  return (
    <Box>
      <Box sx={{ position: 'relative' }}>
        {/* Loading state for edit mode */}
        {isEditModeLoading && !loadingTimeout && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: '8px',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                Loading item data...
              </Typography>
              <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '12px' }}>
                {getItemCategory.isLoading ? 'Fetching categories...' : 'Preparing form...'}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Timeout message */}
        {loadingTimeout && mode === 'edit' && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px',
          }}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                Loading is taking longer than expected...
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setLoadingTimeout(false);
                  // Force re-initialization
                  window.location.reload();
                }}
              >
                Retry
              </Button>
            </Box>
          </Box>
        )}

        {/* Debug section - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{
            backgroundColor: '#f5f5f5',
            padding: 2,
            margin: 2,
            borderRadius: 2,
            fontSize: '12px'
          }}>
            <Typography variant="h6">Debug Info:</Typography>
            <Typography>Form Valid: {formik.isValid ? 'Yes' : 'No'}</Typography>
            <Typography>Form Dirty: {formik.dirty ? 'Yes' : 'No'}</Typography>
            <Typography>Form Ready: {isFormReady ? 'Yes' : 'No'}</Typography>
            <Typography>Edit Mode Ready: {isEditModeReady ? 'Yes' : 'No'}</Typography>
            <Typography>Edit Mode Loading: {isEditModeLoading ? 'Yes' : 'No'}</Typography>
            <Typography>Form Errors: {Object.keys(formik.errors).length}</Typography>
            <Typography>Store Data Loaded: {manageStoreData?.data ? 'Yes' : 'No'}</Typography>
            <Typography>Categories Loaded: {categoryList.length > 0 ? 'Yes' : 'No'}</Typography>
            <Typography>Categories Loading: {getItemCategory.isLoading ? 'Yes' : 'No'}</Typography>
            <Typography>Selected Store ID: {selectedStoreId || 'None'}</Typography>
            <Typography>Selected Store Type: {selectedStoreType || 'None'}</Typography>
            <Typography>Selected Store: {formik.values.restaurant || 'None'}</Typography>
            <Typography>Selected Category: {formik.values.category || 'None'}</Typography>
            <Typography>Selected Subcategory: {formik.values.subcategory || 'None'}</Typography>
            <Typography>Images Count: {formik.values.itemImages.length}</Typography>
            {Object.keys(formik.errors).length > 0 && (
              <Box>
                <Typography color="error">Validation Errors:</Typography>
                {Object.entries(formik.errors).map(([key, value]) => (
                  <Typography key={key} color="error">- {key}: {String(value)}</Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        <form
          onSubmit={formik.handleSubmit}
          style={{
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '24px',
            backgroundColor: '#fff',
            margin: 15,
          }}
        >
          {/* Show only Select Store field initially */}
          {renderFormField(formFields.find(field => field.name === 'restaurant')!)}

          {/* Show all other fields only after a store is selected */}
          {formik.values.restaurant && (
            <>
              {formFields.filter(field => field.name !== 'restaurant').map((field) => {
                if (field.name === 'discount') {
                  return (
                    <React.Fragment key={field.name}>
                      {renderFormField(field)}
                      {formik.values.discount === 'Yes' && renderDiscountFields()}
                    </React.Fragment>
                  );
                }
                return renderFormField(field);
              })}

              {/* Veg / Nonveg - Hidden for Pharmacy and Grocery stores */}
              {selectedStoreType !== 'PharmacyStore' && selectedStoreType !== 'GroceryStore' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: '30%', pr: 2 }}>
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: `Veg / Nonveg <span style="color: red">*</span>`,
                      }}
                    />
                  </Box>
                  <Box sx={{ width: '70%' }}>
                    <FormControl
                      error={formik.touched.isVeg && Boolean(formik.errors.isVeg)}
                    >
                      <RadioGroup
                        row
                        name="isVeg"
                        value={formik.values.isVeg}
                        onChange={formik.handleChange}
                      >
                        <FormControlLabel
                          value="Veg"
                          control={<Radio />}
                          label="Veg"
                        />
                        <FormControlLabel
                          value="Nonveg"
                          control={<Radio />}
                          label="Nonveg"
                        />
                      </RadioGroup>
                      <FormHelperText>
                        {formik.touched.isVeg && formik.errors.isVeg}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {/* Tax */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: '30%', pr: 2 }}>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: `Tax <span style="color: red">*</span>`,
                    }}
                  />
                </Box>
                <Box sx={{ width: '70%' }}>
                  <FormControl
                    error={formik.touched.hasTax && Boolean(formik.errors.hasTax)}
                  >
                    <RadioGroup
                      row
                      name="hasTax"
                      value={formik.values.hasTax}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                    <FormHelperText>
                      {formik.touched.hasTax && formik.errors.hasTax}
                    </FormHelperText>
                  </FormControl>
                </Box>
              </Box>

              {/* Conditionally render Tax fields right after Tax */}
              {formik.values.hasTax === 'Yes' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: '30%', pr: 2 }}>
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: `Tax Name <span style="color: red">*</span>`,
                        }}
                      />
                    </Box>
                    <Box sx={{ width: '35%' }}>
                      <TextField
                        fullWidth
                        name="taxName"
                        value={formik.values.taxName}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 50) {
                            handleFieldChange('taxName', value);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        placeholder="Tax Name"
                        error={
                          formik.touched.taxName && Boolean(formik.errors.taxName)
                        }
                        helperText={formik.touched.taxName && formik.errors.taxName}
                        inputProps={{
                          maxLength: 50,
                        }}
                      />
                    </Box>
                    <Box sx={{ width: '35%' }}>
                      <TextField
                        fullWidth
                        name="taxPercentage"
                        value={formik.values.taxPercentage}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleFieldChange('taxPercentage', value);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        placeholder="Tax Percentage"
                        error={
                          formik.touched.taxPercentage &&
                          Boolean(formik.errors.taxPercentage)
                        }
                        helperText={
                          formik.touched.taxPercentage &&
                          formik.errors.taxPercentage
                        }
                        inputProps={{
                          inputMode: 'decimal',
                          // pattern: '^\d{1,2}(\.\d{1,2})?$',
                          maxLength: 5,
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: '30%', pr: 2 }}>
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: `Tax Name (In Arabic) <span style="color: red">*</span>`,
                        }}
                      />
                    </Box>
                    <Box sx={{ width: '70%' }}>
                      <TextField
                        fullWidth
                        name="taxNameArabic"
                        value={formik.values.taxNameArabic}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 50) {
                            handleFieldChange('taxNameArabic', value);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        placeholder="Tax Name (In Arabic)"
                        error={
                          formik.touched.taxNameArabic &&
                          Boolean(formik.errors.taxNameArabic)
                        }
                        helperText={
                          formik.touched.taxNameArabic &&
                          formik.errors.taxNameArabic
                        }
                        inputProps={{
                          maxLength: 50,
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}

              {/* Prescription - Only show for Pharmacy stores */}
              {selectedStoreType === 'PharmacyStore' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: '30%', pr: 2 }}>
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: `Prescription <span style="color: red">*</span>`,
                      }}
                    />
                  </Box>
                  <Box sx={{ width: '70%' }}>
                    <FormControl
                      error={formik.touched.prescription && Boolean(formik.errors.prescription)}
                    >
                      <RadioGroup
                        row
                        name="prescription"
                        value={formik.values.prescription}
                        onChange={formik.handleChange}
                      >
                        <FormControlLabel
                          value="Yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                      <FormHelperText>
                        {formik.touched.prescription && formik.errors.prescription}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {/* Combo Pack */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: '30%', pr: 2 }}>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: `Combo Pack <span style="color: red">*</span>`,
                    }}
                  />
                </Box>
                <Box sx={{ width: '70%' }}>
                  <FormControl
                    error={
                      formik.touched.comboPack && Boolean(formik.errors.comboPack)
                    }
                  >
                    <RadioGroup
                      row
                      name="comboPack"
                      value={formik.values.comboPack}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                    <FormHelperText>
                      {formik.touched.comboPack && formik.errors.comboPack}
                    </FormHelperText>
                  </FormControl>
                </Box>
              </Box>

              {/* Halal - Hidden for Pharmacy and Grocery stores */}
              {selectedStoreType !== 'PharmacyStore' && selectedStoreType !== 'GroceryStore' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: '30%', pr: 2 }}>
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: `Halal <span style="color: red">*</span>`,
                      }}
                    />
                  </Box>
                  <Box sx={{ width: '70%' }}>
                    <FormControl
                      error={formik.touched.halal && Boolean(formik.errors.halal)}
                    >
                      <RadioGroup
                        row
                        name="halal"
                        value={formik.values.halal}
                        onChange={formik.handleChange}
                      >
                        <FormControlLabel
                          value="Yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                      <FormHelperText>
                        {formik.touched.halal && formik.errors.halal}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {/* Item Available Time - Hidden for Pharmacy and Grocery stores */}
              {selectedStoreType !== 'PharmacyStore' && selectedStoreType !== 'GroceryStore' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: '30%', pr: 2 }}>
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: `Item Available Time <span style="color: red">*</span>`,
                      }}
                    />
                  </Box>
                  <Box sx={{ width: '70%' }}>
                    <FormControl
                      error={
                        formik.touched.availableTime &&
                        Boolean(formik.errors.availableTime)
                      }
                    >
                      {['breakfast', 'lunch', 'dinner'].map(
                        (slot) => (
                          <FormControlLabel
                            key={slot}
                            control={
                              <Checkbox
                                checked={formik.values.availableTime.includes(
                                  slot as AddOrEditItemRequestSchemaItemAvailableTimeEnum
                                )}
                                onChange={handleAvailableTimeChange}
                                value={slot}
                              />
                            }
                            label={slot}
                          />
                        )
                      )}
                      <FormHelperText>
                        {formik.touched.availableTime && formik.errors.availableTime}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {[
                { name: 'description', label: 'Description' },
                { name: 'descriptionArabic', label: 'Description in Arabic' },
                { name: 'metaKeywords', label: 'Meta Keywords' },
                { name: 'metaKeywordsArabic', label: 'Meta Keywords Arabic' },
                { name: 'metaDescription', label: 'Meta Description' },
                { name: 'metaDescriptionArabic', label: 'Meta Description Arabic' },
              ].map(({ name, label }) => (
                <Box
                  key={name}
                  sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                >
                  <Box sx={{ width: '30%', pr: 2 }}>
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: `${label} ${['metaKeywords', 'metaKeywordsArabic', 'metaDescription', 'metaDescriptionArabic'].includes(name as string) ? '' : '<span style="color: red">*</span>'}`,
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: '70%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <TextField
                      fullWidth
                      name={name}
                      value={String(formik.values[name as keyof FormValues])}
                      onChange={(e) => {
                        handleFieldChange(name, e.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      multiline
                      rows={5}
                      error={Boolean(
                        formik.touched[name as keyof FormValues] &&
                        formik.errors[name as keyof FormValues]
                      )}
                      sx={{
                        width: '80%',
                        '& .MuiOutlinedInput-root': {
                          width: '100%',
                          height: '250px',
                          padding: 0,
                          alignItems: 'flex-start',
                        },
                        '& .MuiInputBase-input': {
                          paddingTop: '14px',
                          paddingLeft: '14px',
                        },
                      }}
                      placeholder={label}
                      inputProps={{
                        style: {
                          textAlign: 'start',
                          verticalAlign: 'top',
                        },
                      }}
                    />
                    {formik.touched[name as keyof FormValues] &&
                      formik.errors[name as keyof FormValues] && (
                        <FormHelperText
                          error
                          sx={{
                            marginTop: '8px',
                            marginBottom: '0px',
                            marginLeft: '0px',
                            marginRight: '0px',
                            width: '80%',
                          }}
                        >
                          {String(formik.errors[name as keyof FormValues])}
                        </FormHelperText>
                      )}
                  </Box>
                </Box>
              ))}

              {/* Choose Choices */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <Box sx={{ width: '30%', pr: 2 }}>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: `Choose Choices <span style="color: red">*</span>`,
                    }}
                  />
                </Box>
                <Box sx={{ width: '70%' }}>
                  <FormControl
                    error={
                      formik.touched['chooseChoices'] &&
                      Boolean(formik.errors['chooseChoices'])
                    }
                  >
                    <RadioGroup
                      row
                      name={'chooseChoices'}
                      value={formik.values['chooseChoices']}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>

              {/* Item Choice Dropdown - moved just below Choose Choices and only visible/enabled if Yes */}
              {formik.values.chooseChoices === 'Yes' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: '30%', pr: 2 }}>
                    <Typography>
                      Item Choice Group <span style={{ color: 'red' }}>*</span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: '70%' }}>
                    <FormControl
                      error={Boolean(formik.touched.itemChoice) && Boolean(formik.errors.itemChoice)}
                      fullWidth
                      sx={{ width: '401px' }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Select
                          multiple
                          open={choiceSelectOpen}
                          onOpen={() => setChoiceSelectOpen(true)}
                          onClose={() => setChoiceSelectOpen(false)}
                          value={formik.values.itemChoice.map((c) => c.value)}
                          onChange={(event) => {
                            const { value } = event.target;
                            const newValue = typeof value === 'string' ? value.split(',') : value;
                            const selected = choiceOptions.filter((opt) => newValue.includes(opt.value));
                            formik.setFieldTouched('itemChoice', true, true);
                            formik.setFieldValue('itemChoice', selected, true);
                            setChoiceSelectOpen(false); // close dropdown after selection
                          }}
                          input={<OutlinedInput />}
                          displayEmpty
                          renderValue={(selected) => {
                            if ((selected as string[]).length === 0) {
                              return (
                                <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
                                  {choiceOptions.length === 0
                                    ? 'No choice group available'
                                    : 'Select Item Choice'}
                                </span>
                              );
                            }
                            return null;
                          }}
                          sx={{
                            width: '401px',
                            filter: choiceOptions.length === 0 ? 'blur(1px)' : 'none',
                            backgroundColor: choiceOptions.length === 0 ? '#f5f5f5' : 'inherit',
                            minWidth: 'unset',
                          }}
                          disabled={formik.values.chooseChoices !== 'Yes' || choiceOptions.length === 0}
                        >
                          <MenuItem value="" disabled>
                            <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                              {choiceOptions.length === 0
                                ? '-- No choice group available --'
                                : '-- Select Choice --'}
                            </span>
                          </MenuItem>
                          {choiceOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {/* Chips shown in a new row below the select */}
                        {formik.values.itemChoice.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {formik.values.itemChoice.map((choice) => (
                              <Chip
                                key={choice.value}
                                label={choice.label}
                                onDelete={() => {
                                  const newChoices = formik.values.itemChoice.filter((c) => c.value !== choice.value);
                                  formik.setFieldValue('itemChoice', newChoices, true);
                                  formik.setFieldTouched('itemChoice', true, true);
                                }}
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        )}
                        <FormHelperText>
                          {choiceOptions.length === 0
                            ? 'No choice group available for this store'
                            : formik.touched.itemChoice && formik.errors.itemChoice
                              ? String(formik.errors.itemChoice)
                              : ''}
                        </FormHelperText>
                      </Box>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {/* Want to add specification - render AFTER Item Choice Group */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <Box sx={{ width: '30%', pr: 2 }}>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: `Want to add specification <span style="color: red">*</span>`,
                    }}
                  />
                </Box>
                <Box sx={{ width: '70%' }}>
                  <FormControl
                    error={
                      formik.touched['addSpecification'] &&
                      Boolean(formik.errors['addSpecification'])
                    }
                  >
                    <RadioGroup
                      row
                      name={'addSpecification'}
                      value={formik.values['addSpecification']}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>

              {formik.values.addSpecification === 'Yes' && (
                <>
                  {formik.values.specifications.map((_spec, index) =>
                    renderSpecificationField(index)
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                      pr: '24%',
                    }}
                  >
                    <Grid>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          },
                        }}
                        startIcon={<AddIcon sx={{ fontSize: 16, color: '#fff' }} />}
                        onClick={handleAddSpecification}
                      >
                        Add More
                      </Button>
                    </Grid>
                  </Box>
                </>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: '30%', pr: 2 }}>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: `Item Images <span style="color: red">*</span>`,
                    }}
                  />
                </Box>
                <Box sx={{ width: '70%' }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                      variant="contained"
                      component="label"
                      disabled={formik.values.itemImages.length >= 5}
                    >
                      Choose File
                      <input
                        ref={chooseFileInputRef}
                        type="file"
                        hidden
                        name="itemImages"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleChange(e, true)}
                      />
                    </Button>
                  </Box>

                  {formik.touched.itemImages && formik.errors.itemImages && (
                    <FormHelperText error>{formik.errors.itemImages}</FormHelperText>
                  )}

                  <Typography variant="caption" sx={{ mt: 1 }} display="block">
                    Max 5 images (Min 800×800)
                  </Typography>

                  <Typography
                    variant="body2"
                    color={
                      formik.values.itemImages.length > 0 ? 'success.main' : 'error.main'
                    }
                    sx={{ mt: 1 }}
                  >
                    {formik.values.itemImages.length > 0
                      ? `${formik.values.itemImages.length} image(s) selected`
                      : 'No file chosen'}
                  </Typography>

                  {/* Display image thumbnails with individual delete buttons */}
                  {formik.values.itemImages.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        {formik.values.itemImages.map((image, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              border: '1px solid #ddd',
                              borderRadius: 1,
                              padding: 1,
                              backgroundColor: '#f9f9f9',
                            }}
                          >
                            <Box sx={{ width: 80, height: 80, position: 'relative' }}>
                              <img
                                src={image instanceof File ? URL.createObjectURL(image) : image}
                                alt={`Preview ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: 4,
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveImage(index)}
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  backgroundColor: 'error.main',
                                  color: 'white',
                                  width: 20,
                                  height: 20,
                                  '&:hover': {
                                    backgroundColor: 'error.dark',
                                  },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                              Image {index + 1}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          onClick={() => setPreviewOpen(true)}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleClear}
                          sx={{ textTransform: 'uppercase' }}
                        >
                          Clear All
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Preview Item Images
                      <IconButton
                        aria-label="close"
                        onClick={() => setPreviewOpen(false)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: 8,
                          color: (theme) => theme.palette.grey[500],
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent>
                      <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} sx={{ mb: 2 }}>
                        <Tab label="Mobile Image" />
                        <Tab label="Web Image" />
                      </Tabs>

                      {/* Mobile Preview */}
                      {tabIndex === 0 && (
                        <>
                          {images.map((item: string | File, idx: number) => {
                            const url = item instanceof File ? URL.createObjectURL(item) : item;
                            return (
                              <Box key={`mobile-${idx}`} sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
                                <img
                                  src={url}
                                  alt={`Mobile Preview ${idx + 1}`}
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </>
                      )}

                      {/* Web Preview */}
                      {tabIndex === 1 && (
                        <>
                          {images.map((item: string | File, idx: number) => {
                            const url = item instanceof File ? URL.createObjectURL(item) : item;
                            return (
                              <Box key={`web-${idx}`} sx={{ width: '100%', mb: 2 }}>
                                <img
                                  src={url}
                                  alt={`Web Preview ${idx + 1}`}
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    border: '1px solid #ddd',
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </>
                      )}
                    </DialogContent>
                  </Dialog>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: '30%', pr: 2 }}></Box>
                <Box sx={{ width: '70%', display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading || !isFormReady}
                    onClick={() => {
                      console.log('Submit button clicked');
                      console.log('Form is valid:', formik.isValid);
                      console.log('Form is dirty:', formik.dirty);
                      console.log('Form errors:', formik.errors);
                      console.log('Form touched:', formik.touched);
                      console.log('Form ready:', isFormReady);

                      // Force validation
                      formik.validateForm().then((errors) => {
                        console.log('Validation errors:', errors);
                        if (Object.keys(errors).length > 0) {
                          console.error('Form has validation errors, preventing submission');
                          // Mark all fields as touched to show errors
                          const touchedFields: any = {};
                          Object.keys(errors).forEach(key => {
                            touchedFields[key] = true;
                          });
                          formik.setTouched(touchedFields);
                        }
                      });
                    }}
                  >
                    {loading
                      ? mode === 'edit'
                        ? 'Updating...'
                        : 'Submitting...'
                      : mode === 'edit'
                        ? 'Update'
                        : 'Submit'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      formik.resetForm();
                      onClose();
                    }}
                    disabled={loading}
                    sx={{
                      color: 'text.primary',
                      borderColor: 'text.primary',
                      '&:hover': {
                        borderColor: 'text.primary',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </form>
      </Box>
    </Box>
  );
};

export default AddItemForm;

